import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { TrendingUp, Shield, DollarSign, Users } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function SCorporation() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: TrendingUp, title: 'Pass-Through Taxation', description: 'Avoid double taxation with profits passing through to shareholders.' },
    { icon: Shield, title: 'Liability Protection', description: 'Protect personal assets from business liabilities and debts.' },
    { icon: DollarSign, title: 'Tax Savings', description: 'Save on self-employment taxes compared to sole proprietorships.' },
    { icon: Users, title: 'Corporate Structure', description: 'Maintain professional credibility with formal business structure.' },
  ];

  const faqs = [
    { question: 'What is an S Corporation?', answer: 'An S Corporation is a special tax designation that provides pass-through taxation benefits while maintaining the liability protection and structure of a corporation.' },
    { question: 'What are the eligibility requirements?', answer: 'S Corps are limited to 100 shareholders, must have only one class of stock, and shareholders must be U.S. citizens or residents. Not all business types qualify.' },
    { question: 'How is an S Corp different from an LLC?', answer: 'While both offer liability protection and pass-through taxation, S Corps have more formal requirements but may offer greater tax savings for certain businesses.' },
    { question: 'What are the ongoing requirements?', answer: 'S Corps must file annual returns, hold shareholder meetings, maintain corporate minutes, and file payroll taxes for shareholder-employees.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>S Corporation Formation</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Form your S Corporation and enjoy tax benefits with corporate protection.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Form an S Corporation?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Combine tax advantages with corporate structure and protection.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about S Corporation formation.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Ready to Form Your S Corp?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Maximize tax benefits while maintaining corporate liability protection.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="s_corporation" />
      <Footer />
    </div>
  );
}